import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSuppliers } from '@features/suppliers/hooks/useSuppliers';
import CreateSupplierBottomSheet from '@features/suppliers/components/create/CreateSupplierBottomSheet';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { useTutorialStore } from '../../../../application/store/TutorialStore';
import OnboardingOverlayCard from '../../shared/OnboardingOverlayCard';
import OnboardingEntityEmptyRow from '../../shared/OnboardingEntityEmptyRow';
import OnboardingEntityList from '../../shared/OnboardingEntityList';
import OnboardingSkipFooter from '../../shared/OnboardingSkipFooter';

interface SuppliersOnboardingStepProps {
  onDismiss: () => void;
}

const SuppliersOnboardingStep: React.FC<SuppliersOnboardingStepProps> = ({ onDismiss }) => {
  const navigate = useNavigate();
  const { suppliers } = useSuppliers();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const { markStepCompleted, setCurrentStep } = useTutorialStore();
  const [showFinished, setShowFinished] = useState(false);

  const hasSuppliers = suppliers.length > 0;

  useEffect(() => {
    if (!hasSuppliers) return;

    markStepCompleted('suppliers');
    setCurrentStep(4);
    setShowFinished(true);
  }, [hasSuppliers, markStepCompleted, setCurrentStep]);

  const handleAddSupplier = () => {
    openBottomSheet({
      title: 'Create Supplier',
      component: (
        <CreateSupplierBottomSheet
          handleClose={async () => {
            await closeBottomSheet();
          }}
        />
      ),
    });
  };

  if (showFinished || hasSuppliers) {
    return (
      <OnboardingOverlayCard
        title="Your suppliers are set up"
        footer={
          <Button variant="contained" onClick={onDismiss}>
            View suppliers
          </Button>
        }
      >
        <Typography variant="body2">
          You can manage suppliers and create purchase orders from here.
        </Typography>

        <OnboardingEntityList
          items={suppliers.map((supplier) => ({
            id: supplier.id,
            primary: supplier.name,
            secondary:
              [supplier.email, supplier.phone].filter(Boolean).join(' · ') || 'Supplier',
            avatarFallback: supplier.name?.charAt(0)?.toUpperCase() ?? 'S',
            chipLabel: supplier.isActive ? 'Active' : 'Inactive',
            chipColor: supplier.isActive ? 'success' : 'default',
            onClick: () => {
              onDismiss();
              navigate(`/suppliers/${supplier.id}`);
            },
          }))}
        />
      </OnboardingOverlayCard>
    );
  }

  return (
    <OnboardingOverlayCard
      title="Add your first supplier"
      footer={<OnboardingSkipFooter onDismiss={onDismiss} />}
    >
      <Typography variant="body2">
        Add the suppliers you order from so you can create purchase orders and keep stock flowing.
      </Typography>

      <OnboardingEntityEmptyRow
        title="Add your first supplier"
        description="Create a supplier to start placing purchase orders"
        actionLabel="Add supplier"
        onAction={handleAddSupplier}
      />
    </OnboardingOverlayCard>
  );
};

export default SuppliersOnboardingStep;
